import React from 'react';

import { variableListEmpty } from '@aline/common';
import { Typography } from '@mui/material';
import { AlineButton, AlineTooltip } from 'src/components/generic';
import { VariableIcon } from 'src/components/icons/VariableIcon';
import type { VariablePanelState } from 'src/components/variables/document-variables/VariablePanel';
import { useOpenAddInput } from 'src/components/variables/document-variables/VariablePanel';
import { Apollo } from 'src/gql/generated';
import { useDocumentSignatureStatus } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  empty: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(5, 2, 2, 2),
  },
  emptyIcon: {
    color: '#9CA7AB',
    height: 40,
    marginBottom: theme.spacing(2),
    width: 40,
  },
  text: {
    marginBottom: theme.spacing(2),
  },
}));

interface VariablesEmptyProps {
  documentId: UUID;
  state: VariablePanelState;
}

export function VariablesEmpty({ documentId, state }: VariablesEmptyProps): JSX.Element {
  const { classes } = useStyles();
  const { openAddInput } = useOpenAddInput();
  const { document, isSigned, wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const disabledReason = wasSentForSignature
    ? 'Cannot add while signing'
    : isSigned
      ? 'Cannot add after signing'
      : !document
        ? 'Checking authorization'
        : document.requesterRole !== Apollo.PermissionRole.Editor
          ? 'Only document editors can add variables'
          : '';

  return (
    <div className={classes.empty} data-cy={variableListEmpty}>
      <VariableIcon className={classes.emptyIcon} />
      <Typography className={classes.text} variant="subtitle2">
        {state.filterItems.length
          ? 'Add a variable or adjust your filter'
          : 'No variables.  Add a variable below.'}
      </Typography>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineButton
            fullWidth
            color="primary"
            disabled={Boolean(disabledReason)}
            variant="contained"
            onClick={openAddInput}
          >
            Add Variable
          </AlineButton>
        </div>
      </AlineTooltip>
    </div>
  );
}
